import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import ConfirmSheet from '@/components/ConfirmSheet';
import { Building2, CheckCircle2, XCircle, ShieldOff, ShieldCheck, Users, Package } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'all', label: 'All' },
];

function statusBadge(status) {
  const map = {
    pending: 'bg-[#0B4F3F]/10 text-[#0B4F3F]',
    approved: 'bg-[#15803D]/10 text-[#15803D]',
    rejected: 'bg-[#B91C1C]/10 text-[#B91C1C]',
    suspended: 'bg-[#5B6675]/10 text-[#5B6675]',
  };
  return `text-[10px] font-bold rounded-full px-2 py-0.5 ${map[status] || ''}`;
}

export default function AdminOrgs() {
  const { api } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/orgs', { params: { status: filter === 'all' ? undefined : filter } });
      setOrgs(data.orgs || []);
      setCounts(data.counts || {});
    } catch {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.put(`/admin/orgs/${id}`, { status, admin_notes: notes[id] || undefined });
      toast.success(`Org ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-orgs-section">
      <div className="grid grid-cols-4 gap-2">
        {[
          { k: 'pending', label: 'Pending', color: 'text-[#0B4F3F]' },
          { k: 'approved', label: 'Approved', color: 'text-[#15803D]' },
          { k: 'rejected', label: 'Rejected', color: 'text-[#B91C1C]' },
          { k: 'suspended', label: 'Suspend', color: 'text-[#5B6675]' },
        ].map(s => (
          <div key={s.k} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2 text-center">
            <p className={`text-lg font-bold font-['Bricolage_Grotesque'] ${s.color}`}>{counts[s.k] || 0}</p>
            <p className="text-[9px] text-[#5B6675]">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="w-full bg-[#EFF2F7] rounded-xl p-1 h-auto">
          {STATUS_TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`orgs-tab-${t.value}`} className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="bg-[#F6F8FB] rounded-xl h-20 animate-pulse" />
      ) : orgs.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
          <p className="text-sm text-[#5B6675]">No organizations</p>
        </div>
      ) : (
        orgs.map(org => (
          <div key={org.id} data-testid={`admin-org-row-${org.id}`} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0B1620] truncate flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5 text-[#0B4F3F]" />{org.name}
                </p>
                <p className="text-[11px] text-[#5B6675] uppercase tracking-wide">{org.org_type}</p>
                <p className="text-[11px] text-[#5B6675]">Code: <span className="font-mono font-bold">{org.code}</span> · Owner: {org.owner_name || '—'}</p>
              </div>
              <span className={statusBadge(org.status)}>{org.status.toUpperCase()}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-[#EFF2F7] rounded p-2">
                <p className="text-[10px] text-[#5B6675] flex items-center gap-1"><Users className="w-3 h-3" />Farmers</p>
                <p className="text-[#0B1620] font-bold">{org.farmer_count}</p>
              </div>
              <div className="bg-[#EFF2F7] rounded p-2">
                <p className="text-[10px] text-[#5B6675] flex items-center gap-1"><Package className="w-3 h-3" />Produce posts</p>
                <p className="text-[#0B1620] font-bold">{org.produce_count}</p>
              </div>
            </div>
            {org.contact_phone && <p className="text-[11px] text-[#5B6675]">📞 {org.contact_phone}</p>}
            {org.gstin && <p className="text-[11px] text-[#5B6675]">GSTIN: {org.gstin}</p>}
            {org.admin_notes && <p className="text-[11px] bg-[#F6F8FB] rounded p-2 border border-[#E4E8EF]">Notes: {org.admin_notes}</p>}

            {org.status === 'pending' && (
              <div className="space-y-2 pt-2 border-t border-[#E4E8EF]">
                <Textarea
                  data-testid={`admin-org-notes-${org.id}`}
                  placeholder="Admin notes (optional)"
                  value={notes[org.id] || ''}
                  onChange={(e) => setNotes({ ...notes, [org.id]: e.target.value })}
                  rows={2}
                  className="text-xs bg-[#F1F5FA] border-[#E4E8EF] rounded-lg resize-none"
                />
                <div className="flex gap-2">
                  <Button data-testid={`approve-org-${org.id}`} onClick={() => updateStatus(org.id, 'approved')} disabled={busyId === org.id} size="sm" className="flex-1 h-9 bg-[#15803D] text-[#F6F8FB] text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                  </Button>
                  <Button data-testid={`reject-org-${org.id}`} onClick={() => updateStatus(org.id, 'rejected')} disabled={busyId === org.id} size="sm" variant="outline" className="flex-1 h-9 border-[#B91C1C] text-[#B91C1C] text-xs">
                    <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                  </Button>
                </div>
              </div>
            )}
            {org.status === 'approved' && (
              <button
                data-testid={`suspend-org-${org.id}`}
                onClick={() => setSuspendTarget(org)}
                disabled={busyId === org.id}
                className="w-full text-xs text-[#B91C1C] bg-[#B91C1C]/5 hover:bg-[#B91C1C]/10 rounded-lg py-2 flex items-center justify-center gap-1"
              >
                <ShieldOff className="w-3.5 h-3.5" /> Suspend
              </button>
            )}
            {org.status === 'suspended' && (
              <button
                data-testid={`reinstate-org-${org.id}`}
                onClick={() => updateStatus(org.id, 'approved')}
                disabled={busyId === org.id}
                className="w-full text-xs text-[#15803D] bg-[#15803D]/5 hover:bg-[#15803D]/10 rounded-lg py-2 flex items-center justify-center gap-1"
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Reinstate
              </button>
            )}
          </div>
        ))
      )}
      <ConfirmSheet
        open={!!suspendTarget}
        onOpenChange={(o) => !o && setSuspendTarget(null)}
        title="Suspend this organization?"
        description={suspendTarget ? `"${suspendTarget.name}"'s owner will lose org_admin access. Farmers keep their accounts.` : ''}
        confirmLabel="Suspend"
        cancelLabel="Keep active"
        destructive
        icon={<ShieldOff className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={async () => {
          if (!suspendTarget) return;
          await updateStatus(suspendTarget.id, 'suspended');
          setSuspendTarget(null);
        }}
        loading={busyId === suspendTarget?.id}
        testId="suspend-org"
      />
    </div>
  );
}
